import React, { useRef, useState, useEffect, createContext, useContext, useCallback, useMemo, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useComposedRefs } from '../utils/use-compose-refs';
import { isApproximatelyEqual, isGreaterWithThreshold } from './utility';

const Direction = {
	LEFT: 'left',
	RIGHT: 'right'
} as const;

type Directions = (typeof Direction)[keyof typeof Direction];

export type ScrollAreaContextType = {
	viewRef: React.RefObject<HTMLDivElement | null>;
	canScrollLeft: boolean;
	canScrollRight: boolean;
	showScrollButtonsOnMobile: boolean;
	handleScroll: (direction: Directions) => void;
	checkScrollButtons: () => void;
};

const ScrollAreaContext = createContext<ScrollAreaContextType | undefined>(undefined);

type ScrollAreaProviderProps = {
	children: React.ReactNode;
	showScrollIndicator?: boolean;
	showScrollButtonsOnMobile?: boolean;
	scrollDistance?: (viewRef: React.RefObject<HTMLDivElement | null>) => number; // Updated to accept a ref as a parameter
};

const Provider: React.FC<ScrollAreaProviderProps> = ({
	children,
	showScrollIndicator = true,
	showScrollButtonsOnMobile = false,
	scrollDistance
}) => {
	const viewRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const checkScrollButtons = useCallback(() => {
		if (viewRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = viewRef.current;

			setCanScrollLeft(isGreaterWithThreshold(scrollLeft, 0, 1));
			setCanScrollRight(!isApproximatelyEqual(clientWidth + scrollLeft, scrollWidth, 1));
		}
	}, []);

	// Resize observer
	useEffect(() => {
		if (!viewRef.current) return () => {};

		let timeout: NodeJS.Timeout;
		const debouncedCheckScroll = () => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				checkScrollButtons();
			}, 150);
		};

		const resizeObserver = new ResizeObserver(() => {
			debouncedCheckScroll();
		});

		resizeObserver.observe(viewRef.current);

		checkScrollButtons();

		return () => {
			resizeObserver.disconnect();
		};
	}, [checkScrollButtons]);

	// Scroll event listener
	useEffect(() => {
		const node = viewRef.current;
		if (!node) return () => {};

		let timeout: NodeJS.Timeout;
		const debouncedCheckScroll = () => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				checkScrollButtons();
			}, 150);
		};

		node.addEventListener('scroll', debouncedCheckScroll);
		return () => node.removeEventListener('scroll', debouncedCheckScroll);
	}, [checkScrollButtons]);

	// Mutation observer
	useEffect(() => {
		const node = viewRef.current;
		if (!node) return () => {};

		const observer = new MutationObserver(mutationsList => {
			mutationsList.some(mutation => {
				if (mutation.type === 'childList') {
					// Nodes added or removed, update UI
					checkScrollButtons();
					return true;
				}
				return false;
			});
		});

		observer.observe(node, {
			childList: true, // Watch for direct children added/removed
			subtree: false // Only immediate children
		});
		return () => observer.disconnect();
	}, [checkScrollButtons]);

	const handleScroll = useCallback(
		(direction: Directions) => {
			if (viewRef.current) {
				// Default to half the view width if no function is provided
				const scrollAmount = scrollDistance ? scrollDistance(viewRef) : viewRef.current.clientWidth / 2;
				viewRef.current.scrollBy({
					left: direction === Direction.LEFT ? -scrollAmount : scrollAmount,
					behavior: 'smooth'
				});
			}
		},
		[scrollDistance]
	);

	const value = useMemo(
		() => ({
			canScrollLeft,
			canScrollRight,
			handleScroll,
			checkScrollButtons,
			viewRef,
			showScrollButtonsOnMobile,
			showScrollIndicator
		}),
		[
			canScrollLeft,
			canScrollRight,
			handleScroll,
			checkScrollButtons,
			showScrollButtonsOnMobile,
			showScrollIndicator
		]
	);

	return <ScrollAreaContext.Provider value={value}>{children}</ScrollAreaContext.Provider>;
};

const useHorizontalScrollArea = () => {
	const context = useContext(ScrollAreaContext);

	if (context === undefined) {
		throw new Error('useHorizontalScrollArea must be used within a Provider');
	}
	return context;
};

const Wrapper = styled.div`
	display: grid;
	position: relative;
	width: 100%;
	overflow: hidden;
`;

/**
 * @example
 * <Root>
 * 		<HorizontalScrollAreaView>
 * 			{[...Array(10)].map((_, index) => (
 * 					<Item key={index}>Item {index + 1}</Item>
 * 			))}
 * 		</HorizontalScrollAreaView>
 * 		<HorizontalScrollAreaButton direction="left" icon={ICON_CHEVRON_LEFT}  />
 * 		<HorizontalScrollAreaButton direction="right" icon={ICON_CHEVRON_RIGHT} />
 * </Root>
 */
const Root: React.FC<ScrollAreaProviderProps & { className?: string }> = ({ children, className, ...props }) => {
	return (
		<Provider {...props}>
			<Wrapper className={className}>{children}</Wrapper>
		</Provider>
	);
};

const scrollIndicatorStyles = css`
	content: '';
	position: absolute;
	top: 0;
	width: 36px;
	height: 100%;
	opacity: 0;
	transition: opacity 0.2s ease-in;
	z-index: 1;
	pointer-events: none;
`;

type ScrollAreaViewProps = {
	$showFirstScrollIndicator: boolean;
	$showLastScrollIndicator: boolean;
};

const ScrollAreaView = styled.div<ScrollAreaViewProps>`
	display: flex;
	width: 100%;
	overflow-x: scroll;
	overflow-y: hidden;
	scroll-behavior: smooth;
	overscroll-behavior: contain;

	&::-webkit-scrollbar {
		display: none;
	}

	&::before {
		${scrollIndicatorStyles}
		left: 0;
		padding-right: 3px;
		transform: translateX(-1px);
		background-image: linear-gradient(
			to right,
			rgba(var(--scroll-area-scroll-indicator-color), 1),
			65%,
			rgba(var(--scroll-area-scroll-indicator-color), 0)
		);
	}

	&::after {
		${scrollIndicatorStyles}
		right: 0;
		transform: translateX(1px);
		padding-left: 3px;
		background-image: linear-gradient(
			to left,
			rgba(var(--scroll-area-scroll-indicator-color), 1),
			65%,
			rgba(var(--scroll-area-scroll-indicator-color), 0)
		);
	}

	${({ $showFirstScrollIndicator }) =>
		$showFirstScrollIndicator &&
		css`
			&::before {
				opacity: 1;
			}
		`}

	${({ $showLastScrollIndicator }) =>
		$showLastScrollIndicator &&
		css`
			&::after {
				opacity: 1;
			}
		`};
`;

const View = forwardRef<HTMLDivElement, { children: React.ReactNode; className?: string }>(
	({ children, className, ...rest }, forwardedRef) => {
		const { canScrollLeft, canScrollRight, viewRef } = useHorizontalScrollArea();
		const composedRefs = useComposedRefs(forwardedRef, viewRef);

		return (
			<ScrollAreaView
				className={className}
				$showFirstScrollIndicator={canScrollLeft}
				$showLastScrollIndicator={canScrollRight}
				ref={composedRefs}
				{...rest}
			>
				{children}
			</ScrollAreaView>
		);
	}
);

View.displayName = 'HorizontalScrollAreaView';

const StyledScrollButton = styled.button<{ direction: Directions; $showOnMobile?: boolean }>`
	position: absolute;
	z-index: 2;
	top: 50%;
	transform: translateY(-50%);
	${({ direction }) => (direction === Direction.LEFT ? 'left: 0;' : 'right: 0;')}
	background-color: var(--button-secondary-bg, rgba(var(--base-08), 1));
	font: var(--label-regular);
	color: rgba(var(--primary), 1);
	font-size: 24px;
	border: none;
	border-radius: 50%;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	${({ $showOnMobile }) =>
		!$showOnMobile &&
		css`
			@media (pointer: coarse), (max-width: 768px) {
				display: none;
			}
		`}
`;

type ScrollAreaButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
	direction: Directions;
	children?: React.ReactNode;
	icon: React.ReactNode;
};

const Button = forwardRef<HTMLButtonElement, ScrollAreaButtonProps>(
	({ direction, className, onClick, children, icon, ...rest }, ref) => {
		const { canScrollLeft, canScrollRight, handleScroll, showScrollButtonsOnMobile } = useHorizontalScrollArea();
		const visible = direction === Direction.LEFT ? canScrollLeft : canScrollRight;

		const handleClick = useCallback(
			(event: React.MouseEvent<HTMLButtonElement>) => {
				handleScroll(direction);
				if (onClick) {
					onClick(event);
				}
			},
			[direction, handleScroll, onClick]
		);

		if (!visible) return null;

		return (
			<StyledScrollButton
				className={className}
				direction={direction}
				onClick={handleClick}
				ref={ref}
				$showOnMobile={showScrollButtonsOnMobile}
				{...rest}
			>
				{children || icon}
			</StyledScrollButton>
		);
	}
);

Button.displayName = 'HorizontalScrollAreaButton';

const HorizontalScrollArea = {
	Provider,
	Root,
	View,
	Button
};

export default HorizontalScrollArea;
